import { GetOptions, Vendor } from "@/core";
import { repositoryHost } from "@/repositories";
import { serviceHost } from "@/services";
import { useUserStore } from "@/stores/user";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";
import { getNextPageParam } from "../utils/page-params";

const databaseService = serviceHost.getDatabaseService();

const vendorRepository = repositoryHost.getVendorRepository(databaseService);
type CreateVendorParams = Parameters<typeof vendorRepository.create>[0];
type UpdateVendorParams = Parameters<typeof vendorRepository.update>[0];
type DeleteVendorParams = Parameters<typeof vendorRepository.delete>[0];

export const useVendors = (options: GetOptions<Vendor>) => {
  return useInfiniteQuery({
    queryKey: ["vendors", "get", JSON.stringify(options)],
    queryFn: ({ pageParam }) =>
      vendorRepository.getAll({
        ...options,
        pagination: {
          ...options.pagination,
          cursor: pageParam,
        },
      }),
    initialPageParam: "",
    getNextPageParam: getNextPageParam(options),
  });
};

/**
 * Loads every vendor by eagerly paginating through all pages. Firestore has no
 * case-insensitive substring search, so owner/phone/email lookups and vendor
 * pickers filter locally — which requires the full vendor set to be present so
 * every vendor is findable, not just the most-recently-updated page.
 * `hasNextPage` stays true until the last page is in, so callers can treat it as
 * "still loading".
 */
export const useAllVendors = () => {
  const query = useVendors({
    pagination: { limit: 200 },
    orderBy: { field: "updatedAt", direction: "desc" },
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return query;
};

export const useVendorById = (vendorId: string) => {
  return useQuery({
    queryKey: ["vendors", "get-by-id", vendorId],
    enabled: vendorId.length > 0,
    queryFn: () => vendorRepository.get({ id: vendorId }),
  });
};

export const useCreateVendor = () => {
  const queryClient = useQueryClient();
  const { hasClientVendorWriteAccess } = useUserStore();

  return useMutation<string, Error, CreateVendorParams>({
    mutationKey: ["vendors", "create"],
    mutationFn: async (params: CreateVendorParams) => {
      if (!hasClientVendorWriteAccess()) {
        throw new Error("Access denied: You do not have permission to create vendors");
      }

      return vendorRepository.create(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
};

export const useUpdateVendor = () => {
  const queryClient = useQueryClient();
  const { hasClientVendorWriteAccess } = useUserStore();

  return useMutation<void, Error, UpdateVendorParams>({
    mutationKey: ["vendors", "update"],
    mutationFn: async (params: UpdateVendorParams) => {
      if (!hasClientVendorWriteAccess()) {
        throw new Error("Access denied: You do not have permission to update vendors");
      }

      return vendorRepository.update(params);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      queryClient.invalidateQueries({
        queryKey: ["vendors", "get-by-id", variables.id],
      });
    },
  });
};

export const useDeleteVendor = () => {
  const queryClient = useQueryClient();
  const { hasFullAccess } = useUserStore();

  return useMutation<void, Error, DeleteVendorParams>({
    mutationKey: ["vendors", "delete"],
    mutationFn: async (params: DeleteVendorParams) => {
      if (!hasFullAccess()) {
        throw new Error("Access denied: Full access required to delete vendors");
      }

      return vendorRepository.delete(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
};
